import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";

export const AssignSkuDialog = ({ label, open, onOpenChange, onSaved }) => {
  const [sku, setSku] = useState("");
  const [wh, setWh] = useState("");
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!label || !open) return;
    setSku(label.sku_id || "");
    setWh(label.warehouse_id || "");
    api.get(`/labels/${label.id}/text`).then((r) => setText(r.data.text)).catch(() => setText(""));
  }, [label, open]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { sku_id: sku };
      if (wh.trim()) body.warehouse_id = wh;
      await api.patch(`/labels/${label.id}`, body);
      if (sku.trim() && wh.trim()) await api.post("/mappings", { sku_id: sku.trim(), warehouse_id: wh.trim() });
      toast.success("Label updated");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="assign-sku-dialog" className="max-w-2xl border-slate-800 bg-[#111827]">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-bold uppercase tracking-tight">Assign SKU / Warehouse</DialogTitle>
          <DialogDescription className="text-slate-400">Page {label ? label.page_index + 1 : ""} of {label?.file_name}. Saving with both fields also adds the mapping for future uploads.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="font-mono text-[11px] uppercase tracking-widest text-slate-400">SKU ID</Label>
            <Input data-testid="assign-sku-input" value={sku} onChange={(e) => setSku(e.target.value)} className="font-mono border-slate-700 bg-slate-950" />
          </div>
          <div className="space-y-1.5">
            <Label className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Warehouse ID</Label>
            <Input data-testid="assign-warehouse-input" value={wh} onChange={(e) => setWh(e.target.value)} className="font-mono border-slate-700 bg-slate-950" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Extracted label text</Label>
          <pre data-testid="assign-label-text" className="scrollbar-thin max-h-48 overflow-auto rounded-sm border border-slate-800 bg-slate-950 p-3 font-mono text-[11px] leading-relaxed text-slate-300 whitespace-pre-wrap">{text || "(no text could be extracted from this page)"}</pre>
        </div>
        <DialogFooter>
          <Button variant="outline" className="border-slate-700" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="assign-save-button" className="bg-blue-600 hover:bg-blue-700" disabled={saving} onClick={save}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
